import React from 'react'
import {useApolloClient} from '@apollo/client'
import PropTypes from 'prop-types'

import ProductSelection from './views/ProductSelection'
import Authorisation from './views/Authorisation'
import Payment from './views/Payment'
import Confirmation from './views/Confirmation'
import {datadogLogger, setJourneyContext} from './datadogLogger/logger'
import { vfastProxyUrl, vfastProxyKey, directProxyUrl, directProxyKey } from './config/urls'
import { sendDirectProxy } from './utils/sendDirectProxy'
import { PILOT_SITE_IDS, SITE_ID_TO_PARK } from './config/parks'
import Container from '@parkholidays/legacy_components/dist/container'
import Typography from '@parkholidays/legacy_components/dist/typography'
import Card, { CardContent } from '@parkholidays/legacy_components/dist/card'
import withCheckoutLayout from './layouts/withCheckoutLayout'
import CheckoutHeader from './components/CheckoutHeader'
import {GET_BOOKING, GENERATE_WIFI_VOUCHER} from './queries'
import { generateId } from './utils/generateId'

const getDummyBookingReference = (parkCode) => parkCode

const InitErrorPage = withCheckoutLayout(({ message }) => (
    <>
        <CheckoutHeader>
            <Container>
                <Typography as="h1" noMargins>Wi-Fi Access</Typography>
            </Container>
        </CheckoutHeader>
        <Container>
            <Card color="white" elevation={3}>
                <CardContent>
                    <Typography as="p" color="red">{message}</Typography>
                </CardContent>
            </Card>
        </Container>
    </>
))

export default function App({isProduction, isStaging}) {
    React.useEffect(() => {
        const localJourneyContext = localStorage.getItem('journeyContext')
        if (!localJourneyContext) {
            const journeyId = generateId()
            setJourneyContext(journeyId)
            localStorage.setItem('journeyContext', journeyId)
        } else {
            setJourneyContext(localJourneyContext)
        }
    }, [])

    const [state, setState] = React.useState({
        stage: 1,
        initError: '',
        siteId: '',
        isToBeFlow: false,
        reference: '',
        parkCode: '',
        parkId: '',
        selectedProduct: undefined,
        voucher: '',
        voucherValidFrom: '',
        voucherValidTo: '',
        transactionId: null,
        customerEmail: null,
        errorMessage: '',
        clientMac: '',
        apMac: '',
    })
    const client = useApolloClient()

    const handleError = (errorMessage) => {
        setState(prevState => ({
            ...prevState,
            errorMessage,
        }))
    }

    React.useEffect(() => {
        const setupCheckout = async () => {
            const queryString = new URLSearchParams(window.location.search)
            const clientMac = queryString.get('client_mac')
            const apMac = queryString.get('ap_mac')
            const siteId = queryString.get('site_id') || ''

            if ((isProduction || isStaging) && (!clientMac || !apMac)) {
                const referrer = document.referrer || 'none'
                const isCaptivePortal = referrer.includes('vfastpark.co.uk')
                datadogLogger.error(`setupCheckout: missing MAC addresses clientMac-${clientMac} apMac-${apMac} referrer-${referrer} via-${isCaptivePortal ? 'captive-portal' : 'direct'}`)
                setState(prevState => ({
                    ...prevState,
                    initError: 'This page must be accessed via the park Wi-Fi network. Please connect to the park Wi-Fi and try again.',
                }))
                return
            }

            const isToBeFlow = PILOT_SITE_IDS.has(siteId)
            const park = SITE_ID_TO_PARK[siteId]
            const parkCode = park?.parkCode || ''

            datadogLogger.info(`setupCheckout: siteId-${siteId} isToBeFlow-${isToBeFlow} parkCode-${parkCode} clientMac-${clientMac} apMac-${apMac}`)

            setState(prevState => ({
                ...prevState,
                clientMac,
                apMac,
                siteId,
                isToBeFlow,
                ...(isToBeFlow && { parkCode }),
            }))

            // To-Be: call direct-to-controller Lambda immediately for 15 mins free WiFi
            if (isToBeFlow && (isProduction || isStaging)) {
                const requestId = generateId()
                try {
                    await sendDirectProxy({
                        url: directProxyUrl,
                        key: directProxyKey,
                        payload: { park: parkCode, siteId, guest: { client_mac: clientMac, ap_mac: apMac, minutes: 15 } },
                        requestId,
                        context: { parkCode, siteId, clientMac, apMac },
                        logger: datadogLogger,
                    })
                    datadogLogger.info('setupCheckout: direct proxy success - 15min WiFi granted', { siteId, parkCode, clientMac, apMac, requestId })
                } catch (_) {
                    handleError('Could not establish Internet Access. Please try again later or contact the Park Reception.')
                }
            }
        }

        setupCheckout()
    }, [isProduction, isStaging])

    const onHandleProductSelection = (product) => {
        setState({
            ...state,
            // To-Be: skip Authorisation, go straight to Payment
            // As-Is: go to Authorisation unless booking already validated
            stage: state.isToBeFlow ? 3 : (!state.reference ? 2 : 3),
            selectedProduct: product,
        })
    }

    const onHandleHolidayBookingValidation = async (reference) => {
        try {
            const {
                data: {validateHolidayBooking},
            } = await client.query({
                query: GET_BOOKING,
                variables: { bookingNo: reference },
            })

            if (validateHolidayBooking) {
                const { valid, park } = validateHolidayBooking
                if (valid && park) {
                    const { code, id } = park
                    const { clientMac, apMac } = state

                    datadogLogger.info(`about to call guests code-${code} id-${id} clientMac-${clientMac} apMac-${apMac}`)
                    if (isProduction || isStaging) {
                        const xhr = new XMLHttpRequest()
                        xhr.open('POST', vfastProxyUrl, true)
                        xhr.setRequestHeader('Content-Type', 'application/json')
                        xhr.setRequestHeader('x-lambda-key', vfastProxyKey)
                        xhr.ontimeout = () => {
                            datadogLogger.info(`ontimeout failure vfast-response responseText-${xhr.responseText} status-${xhr.status}`)
                        }
                        xhr.onload = () => {
                            if (xhr.status !== 200) {
                                datadogLogger.error(`VFast guests POST unexpected status-${xhr.status} responseText-${xhr.responseText}`)
                                handleError('Could not establish Internet Access. Please try again later or contact the Park Reception.')
                                return
                            }
                            try {
                                const {status} = JSON.parse(xhr.responseText)
                                if (status === 'ok') {
                                    setState(prevState => ({
                                        ...prevState,
                                        stage: 3,
                                        reference,
                                        parkCode: code,
                                        parkId: id,
                                    }))
                                } else {
                                    datadogLogger.error(`VFast guests POST unexpected status in response body: ${xhr.responseText}`)
                                    handleError('Could not establish Internet Access. Please try again later or contact the Park Reception.')
                                }
                            } catch (e) {
                                datadogLogger.error(`VFast guests POST failed to parse response: ${xhr.responseText} error: ${e?.message || e}`)
                                handleError('Could not establish Internet Access. Please try again later or contact the Park Reception.')
                            }
                        }
                        xhr.onerror = () => {
                            const textResponses = ["", "text"]
                            if (textResponses.includes(xhr.responseType)) {
                                datadogLogger.info(`onerror failure text response type vfast-response- ${xhr.responseText}`)
                            }
                            if (xhr.responseType === "json") {
                                datadogLogger.info(`onerror failure json response type vfast-response- ${JSON.parse(xhr.responseText)}`)
                            }
                            datadogLogger.info('error hit handler xhr.onerror')
                            datadogLogger.error(`VFast guests POST onerror: responseText-${xhr.responseText} status-${xhr.status}`)
                            handleError('Could not establish Internet Access. Please try again later or contact the Park Reception.')
                        }
                        xhr.send(JSON.stringify({
                            park: code,
                            guest: { client_mac: clientMac, ap_mac: apMac, minutes: 15 },
                        }))
                    } else {
                        setState(prevState => ({
                            ...prevState,
                            stage: 3,
                            reference,
                            parkCode: code,
                            parkId: id,
                        }))
                    }
            } else {
                    if (valid && !park) {
                        datadogLogger.error(`onHandleHolidayBookingValidation: valid booking but park is null reference-${reference}`)
                    } else {
                        datadogLogger.info('INVALID')
                    }
                    handleError("Couldn't validate your booking number, please check and try again")
                }
            }
        } catch (error) {
            datadogLogger.error(`onHandleHolidayBookingValidation error: ${error?.message || error}`)
            handleError(error?.message || 'Error on holiday booking validation.')
        }
    }

    const onHandlePaymentSuccess = async (chargeId, customerEmail) => {
        const { reference, selectedProduct: {duration}, parkCode, isToBeFlow } = state
        const bookingNo = isToBeFlow ? getDummyBookingReference(parkCode) : reference
        const requestId = generateId()
        const MAX_VOUCHER_ATTEMPTS = 3
        const VOUCHER_BACKOFF_MS = [0, 1000, 3000]

        datadogLogger.info('voucher: generation:start', { requestId, bookingNo, parkCode, duration })

        let lastError
        for (let attempt = 1; attempt <= MAX_VOUCHER_ATTEMPTS; attempt++) {
            if (attempt > 1) {
                await new Promise(r => setTimeout(r, VOUCHER_BACKOFF_MS[attempt - 1]))
                datadogLogger.warn('voucher: generation:retry', {
                    requestId,
                    attempt: attempt - 1,
                    nextAttempt: attempt,
                    bookingNo,
                    parkCode,
                    duration,
                    errorMessage: lastError?.message || String(lastError),
                })
            }

            datadogLogger.info('voucher: generation:attempt', {
                requestId,
                attempt,
                maxAttempts: MAX_VOUCHER_ATTEMPTS,
                bookingNo,
                parkCode,
                duration,
            })

            const startMs = Date.now()
            try {
                const {
                    data: {createHolidayGuestWifiVoucher},
                } = await client.mutate({
                    mutation: GENERATE_WIFI_VOUCHER,
                    context: {clientName: 'holidayGuestWifiVoucherLink'},
                    variables: {
                        createHolidayGuestWifiVoucherInput: {
                            bookingNo,
                            duration,
                            parkCode,
                            requestId,
                        },
                    },
                })

                if (
                    createHolidayGuestWifiVoucher &&
                    Object.prototype.hasOwnProperty.call(createHolidayGuestWifiVoucher, 'voucher')
                ) {
                    const {voucher, validFrom, validTo} = createHolidayGuestWifiVoucher
                    datadogLogger.info('voucher: generation:success', {
                        requestId,
                        attempt,
                        durationMs: Date.now() - startMs,
                        bookingNo,
                        parkCode,
                        duration,
                    })
                    setState(prevState => ({
                        ...prevState,
                        voucher,
                        voucherValidFrom: validFrom,
                        voucherValidTo: validTo,
                        transactionId: chargeId || null,
                        customerEmail: customerEmail || null,
                        stage: 4,
                    }))
                    return
                }

                // Lambda returned null or a response without voucher — treat as retryable.
                const noVoucherErr = new Error('Payment was successful but we were unable to generate your Wi-Fi voucher. Please contact Park Reception.')
                datadogLogger.error('voucher: generation:error', {
                    requestId,
                    attempt,
                    durationMs: Date.now() - startMs,
                    bookingNo,
                    parkCode,
                    duration,
                    errorType: 'no-voucher-in-response',
                    errorCategory: 'VOUCHER_UNKNOWN',
                })
                lastError = noVoucherErr

            } catch (error) {
                const networkStatus = error?.networkError?.statusCode
                const voucherErrorCategory = error?.networkError
                    ? networkStatus >= 500 ? 'VOUCHER_HTTP_5XX'
                      : networkStatus >= 400 ? 'VOUCHER_HTTP_4XX'
                      : 'VOUCHER_NETWORK_ERROR'
                    : error?.graphQLErrors?.length ? 'VOUCHER_GRAPHQL_ERROR'
                    : 'VOUCHER_UNKNOWN'
                datadogLogger.error('voucher: generation:error', {
                    requestId,
                    attempt,
                    durationMs: Date.now() - startMs,
                    bookingNo,
                    parkCode,
                    duration,
                    errorType: error?.networkError ? 'network' : 'graphql',
                    errorCategory: voucherErrorCategory,
                    errorMessage: error?.message || String(error),
                })
                lastError = error
            }
        }

        datadogLogger.error('voucher: generation:failed', {
            requestId,
            attempts: MAX_VOUCHER_ATTEMPTS,
            bookingNo,
            parkCode,
            duration,
            errorMessage: lastError?.message || String(lastError),
        })
        throw lastError
    }

    const stages = {
        1: <ProductSelection onHandleProductSelection={onHandleProductSelection}/>,
        2: (
            <Authorisation
                onHandleHolidayBookingValidation={onHandleHolidayBookingValidation}
                errorMessage={state.errorMessage}
            />
        ),
        3: (
            <Payment
                selectedProduct={state.selectedProduct}
                onHandlePaymentSuccess={onHandlePaymentSuccess}
                isProduction={isProduction}
                parkCode={state.parkCode}
            />
        ),
        4: (
            <Confirmation
                isProduction={isProduction}
                isStaging={isStaging}
                isToBeFlow={state.isToBeFlow}
                selectedProduct={state.selectedProduct}
                voucher={state.voucher}
                validFrom={state.voucherValidFrom}
                validTo={state.voucherValidTo}
                parkCode={state.parkCode}
                clientMac={state.clientMac}
                apMac={state.apMac}
                siteId={state.siteId}
                proxyUrl={state.isToBeFlow ? directProxyUrl : vfastProxyUrl}
                proxyKey={state.isToBeFlow ? directProxyKey : vfastProxyKey}
                transactionId={state.transactionId}
                customerEmail={state.customerEmail}
            />
        ),
    }

    if (state.initError) {
        return <InitErrorPage message={state.initError} />
    }

    if (state.errorMessage) {
        return <InitErrorPage message={state.errorMessage} />
    }

    return stages[state.stage]
}

App.propTypes = {
    isProduction: PropTypes.bool,
    isStaging: PropTypes.bool,
}
